import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { t } from '../../../i18n/uiText';
import {
  fetchGuestVerseInsight,
  getCachedGuestVerseInsight,
  prefetchGuestVerseInsights,
} from '../../../services/guestVerseInsight';
import {
  formatAssistantInsightHtml,
  wrapInsightProseHtml,
} from '../../../utils/assistantInsightHtml';
import {
  guestInsightSnapshotFingerprint,
  guestInsightSnapshotForMode,
} from '../../../utils/guestInsightPayload';
import { bfHaptic } from './BfHooks';

const MODE_LABELS = {
  simple: () => t('homeInsightExplain') || 'Объяснение',
  application: () => t('application'),
};

const NEXT_MODE = {
  simple: 'application',
};

const PREV_MODE = {
  application: 'simple',
};

/**
 * Разбор внутри той же карточки «Стих дня» — без отдельного попапа.
 */
export default function BfHomeInsightPanel({
  mode,
  reference,
  bookId,
  chapter,
  verse,
  translation,
  onClose,
  onComplete,
  onGoToVerse,
  onSwitchMode,
}) {
  const bodyRef = useRef(null);
  const contextAnswerRef = useRef('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [text, setText] = useState('');
  const [loadedMode, setLoadedMode] = useState(null);

  const insightRequestParams = useCallback(
    (targetMode) => {
      const contextAnswerText = contextAnswerRef.current;
      const snapshot = guestInsightSnapshotForMode(targetMode, contextAnswerText);
      return {
        bookId,
        chapter,
        verse,
        mode: targetMode,
        translation,
        contextAnswerText,
        snapshotFingerprint: guestInsightSnapshotFingerprint(snapshot),
      };
    },
    [bookId, chapter, verse, translation]
  );

  const label = useMemo(() => (mode && MODE_LABELS[mode] ? MODE_LABELS[mode]() : ''), [mode]);
  const nextMode = mode ? NEXT_MODE[mode] : null;
  const prevMode = mode ? PREV_MODE[mode] : null;
  const nextLabel = nextMode && MODE_LABELS[nextMode] ? MODE_LABELS[nextMode]() : null;
  const prevLabel = prevMode && MODE_LABELS[prevMode] ? MODE_LABELS[prevMode]() : null;

  const prefetchAfterMode = useCallback(
    (doneMode, contextAnswerText) => {
      if (doneMode === 'simple' && contextAnswerText) {
        prefetchGuestVerseInsights({
          bookId,
          chapter,
          verse,
          translation,
          contextAnswerText,
          modes: ['application'],
        });
      }
    },
    [bookId, chapter, verse, translation]
  );

  const loadMode = useCallback(
    async (targetMode, { silent = false } = {}) => {
      if (!targetMode || !bookId || !chapter || !verse) return;

      const simpleCached = getCachedGuestVerseInsight({
        bookId,
        chapter,
        verse,
        mode: 'simple',
        translation,
        snapshotFingerprint: '0',
      });
      if (simpleCached?.text) contextAnswerRef.current = simpleCached.text;

      const params = insightRequestParams(targetMode);
      const cached = getCachedGuestVerseInsight(params);
      if (cached?.text) {
        setText(cached.text);
        setLoadedMode(cached.mode || targetMode);
        setError(null);
        if (!silent) setLoading(false);
      } else if (!silent) {
        setLoading(true);
        setError(null);
        setText('');
      }

      if (cached?.text) {
        if (targetMode === 'simple') contextAnswerRef.current = cached.text;
        prefetchAfterMode(targetMode, contextAnswerRef.current);
        return;
      }

      try {
        const result = await fetchGuestVerseInsight(params);
        setText(result.text);
        setLoadedMode(result.mode || targetMode);
        setError(null);
        if (targetMode === 'simple' && result.text) contextAnswerRef.current = result.text;
        prefetchAfterMode(targetMode, contextAnswerRef.current);
      } catch (err) {
        if (!cached?.text) {
          if (err?.code === 'GUEST_INSIGHT_LIMIT_REACHED') {
            setError(
              t('guestInsightLimit') ||
                'Сегодня лимит бесплатных разборов исчерпан. Войдите по email — лимит выше.'
            );
          } else {
            setError(err?.message || t('error'));
          }
        }
      } finally {
        setLoading(false);
      }
    },
    [bookId, chapter, verse, translation, insightRequestParams, prefetchAfterMode]
  );

  useEffect(() => {
    contextAnswerRef.current = '';
  }, [bookId, chapter, verse, translation]);

  useEffect(() => {
    if (!mode) return;
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
    const cached = getCachedGuestVerseInsight(insightRequestParams(mode));
    if (!cached?.text) {
      setText('');
      setLoadedMode(null);
    }
    void loadMode(mode, { silent: Boolean(cached?.text) });
  }, [mode, bookId, chapter, verse, translation, loadMode]);

  const handleCloseToHome = () => {
    const doneMode = loadedMode || mode;
    if (text?.trim() && !error && doneMode) onComplete?.(doneMode);
    onClose?.();
  };

  const handleHeaderBack = () => {
    bfHaptic('light');
    if (prevMode) {
      onSwitchMode?.(prevMode);
      return;
    }
    handleCloseToHome();
  };

  const handleNext = () => {
    if (!nextMode) return;
    const doneMode = loadedMode || mode;
    if (text?.trim() && !error && doneMode) onComplete?.(doneMode);
    bfHaptic('light');
    onSwitchMode?.(nextMode);
  };

  const headerBackLabel = prevLabel
    ? `${t('back') || 'Назад'}: ${prevLabel}`
    : t('close') || 'Закрыть';

  const html = wrapInsightProseHtml(formatAssistantInsightHtml(text, { translation }));

  const backFootLabel = prevMode && prevLabel ? prevLabel : t('close') || 'Закрыть';

  return (
    <div className="bf-home-spot__insight" aria-label={label}>
      <header className="bf-home-spot__insight-head bf-home-spot__insight-head--static">
        <div className="bf-home-spot__insight-head-text">
          <span className="bf-home-spot__insight-eyebrow">{reference}</span>
          <h2 className="bf-home-spot__insight-title">{label}</h2>
        </div>
      </header>

      <div className="bf-home-spot__insight-body" ref={bodyRef}>
        {loading && !text && (
          <p className="bf-home-spot__insight-status">{t('chatLoadingAnswer')}</p>
        )}
        {!loading && error && !text && (
          <p className="bf-home-spot__insight-status bf-home-spot__insight-status--error">
            {error}
          </p>
        )}
        {html && (
          <div
            className="bf-home-spot__insight-content"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        )}
      </div>

      <footer className="bf-home-spot__insight-foot">
        <nav className="bf-home-spot__insight-nav" aria-label={t('verseInsightDialogAria') || 'Разбор'}>
          <button
            type="button"
            className="bf-home-spot__insight-prev"
            onClick={handleHeaderBack}
            aria-label={headerBackLabel}
          >
            <span className="bf-home-spot__link-chev bf-home-spot__link-chev--left" aria-hidden="true" />
            <span>{backFootLabel}</span>
          </button>
          <span className="bf-home-spot__insight-nav-spacer" aria-hidden="true" />
          {nextMode && nextLabel ? (
            <button
              type="button"
              className="bf-home-spot__insight-next"
              onClick={handleNext}
              disabled={loading || !text?.trim() || !!error}
            >
              <span>{nextLabel}</span>
              <span className="bf-home-spot__link-chev" aria-hidden="true" />
            </button>
          ) : onGoToVerse ? (
            <button
              type="button"
              className="bf-home-spot__insight-next"
              onClick={onGoToVerse}
              aria-label={t('contextSearchGoToRef')}
            >
              <span>{t('contextSearchGoToRef') || 'В Библии'}</span>
              <span className="bf-home-spot__link-chev" aria-hidden="true" />
            </button>
          ) : null}
        </nav>
      </footer>
    </div>
  );
}
